import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/AdminLayout';
import ImageUpload from '@/components/ImageUpload';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2 } from 'lucide-react';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title_tr: '',
    title_en: '',
    content_tr: '',
    content_en: '',
    excerpt_tr: '',
    excerpt_en: '',
    image_url: '',
    slug: '',
    author: '',
    published: false,
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData);
        toast.success('Blog yazısı güncellendi!');
      } else {
        await axios.post(`${API}/blog`, formData);
        toast.success('Blog yazısı eklendi!');
      }
      fetchPosts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Bir hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu blog yazısını silmek istediğinizden emin misiniz?')) return;

    try {
      await axios.delete(`${API}/blog/${id}`);
      toast.success('Blog yazısı silindi!');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Bir hata oluştu!');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title_tr: post.title_tr,
      title_en: post.title_en,
      content_tr: post.content_tr,
      content_en: post.content_en,
      excerpt_tr: post.excerpt_tr,
      excerpt_en: post.excerpt_en,
      image_url: post.image_url,
      slug: post.slug,
      author: post.author,
      published: post.published,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setFormData({
      title_tr: '',
      title_en: '',
      content_tr: '',
      content_en: '',
      excerpt_tr: '',
      excerpt_en: '',
      image_url: '',
      slug: '',
      author: '',
      published: false,
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, published: checked });
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 
            className="text-3xl font-bold text-gray-900"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="blog-title"
          >
            Blog Yönetimi
          </h1>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-blog-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Yazı
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPost ? 'Yazı Düzenle' : 'Yeni Yazı Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="title_tr">Başlık (TR)</Label>
                    <Input
                      id="title_tr"
                      name="title_tr"
                      value={formData.title_tr}
                      onChange={handleChange}
                      required
                      data-testid="blog-title-tr"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title_en">Title (EN)</Label>
                    <Input
                      id="title_en"
                      name="title_en"
                      value={formData.title_en}
                      onChange={handleChange}
                      required
                      data-testid="blog-title-en"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="excerpt_tr">Özet (TR)</Label>
                  <Textarea
                    id="excerpt_tr"
                    name="excerpt_tr"
                    value={formData.excerpt_tr}
                    onChange={handleChange}
                    required
                    rows={2}
                    data-testid="blog-excerpt-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="excerpt_en">Excerpt (EN)</Label>
                  <Textarea
                    id="excerpt_en"
                    name="excerpt_en"
                    value={formData.excerpt_en}
                    onChange={handleChange}
                    required
                    rows={2}
                    data-testid="blog-excerpt-en"
                  />
                </div>
                <div>
                  <Label htmlFor="content_tr">İçerik (TR)</Label>
                  <Textarea
                    id="content_tr"
                    name="content_tr"
                    value={formData.content_tr}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="blog-content-tr"
                  />
                </div>
                <div>
                  <Label htmlFor="content_en">Content (EN)</Label>
                  <Textarea
                    id="content_en"
                    name="content_en"
                    value={formData.content_en}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="blog-content-en"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="slug">Slug (URL)</Label>
                    <Input
                      id="slug"
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      required
                      placeholder="example-post-title"
                      data-testid="blog-slug"
                    />
                  </div>
                  <div>
                    <Label htmlFor="author">Yazar</Label>
                    <Input
                      id="author"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      required
                      data-testid="blog-author"
                    />
                  </div>
                </div>
                <ImageUpload
                  label="Blog Görseli"
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                />
                <div className="flex items-center space-x-2">
                  <Switch
                    id="published"
                    checked={formData.published}
                    onCheckedChange={handleSwitchChange}
                    data-testid="blog-published"
                  />
                  <Label htmlFor="published">Yayınla</Label>
                </div>
                <Button type="submit" disabled={loading} className="w-full" data-testid="blog-submit">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-6 flex gap-6" data-testid={`blog-card-${post.id}`}>
              <img src={post.image_url} alt={post.title_tr} className="w-48 h-32 object-cover rounded-lg" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">{post.title_tr}</h3>
                  {post.published && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Yayında</span>
                  )}
                </div>
                <p className="text-gray-600 mb-2 line-clamp-2">{post.excerpt_tr}</p>
                <p className="text-sm text-gray-500 mb-4">Yazar: {post.author}</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(post)} data-testid={`edit-blog-${post.id}`}>
                    <Edit className="w-4 h-4 mr-1" />
                    Düzenle
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(post.id)} data-testid={`delete-blog-${post.id}`}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Sil
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminBlog;